import React from "react"
import styled from "styled-components"
import { Card, List, Skeleton } from "antd"
import { IMoney } from "platform-client/types"
import { MoneyDisplay } from "components/display/MoneyDisplay"
import { IPremiumBreakdown } from "models/policies/totalPaid"
import { IAdditionalProduct } from "models/policies/additionalProduct"
import { RoadsideAssistanceMappings } from "utils/data/stella-roadside-mapping"
import { BrokerCommissionAdjuster } from "containers/quotes/views/Quote/components/quote-premium/broker-commission-adjuster"

const ListStyledFooter = styled.div`
  padding: 8px;
  display: flex;
  background-color: #fafafa;
  justify-content: space-between;
  font-weight: 600;
`

export const PolicyPremium: React.FunctionComponent<IQuotePremium> = ({
  loading = false,
  gross,
  net,
  brokerCommission,
  brokerCommissionPercent,
  iptPercentage,
  iptTotal,
  premiumBreakdown,
  additionalProducts,
  feesTotal
}) => {
  let insurancePremium = premiumBreakdown?.value.net
  if (insurancePremium && brokerCommission) {
    insurancePremium = {
      value: insurancePremium.value - brokerCommission.value,
      currencyCode: insurancePremium.currencyCode ?? "AUD"
    }
  }

  return (
    <Card title="Premium" loading={loading}>
      <List
        itemLayout="horizontal"
        size="small"
        footer={
          <ListStyledFooter>
            <span>Total</span>
            {gross && (
              <span>
                <MoneyDisplay value={gross} />
              </span>
            )}
          </ListStyledFooter>
        }
      >
        <List.Item style={{ padding: 8 }}>
          <Skeleton title={false} loading={!insurancePremium} active>
            {insurancePremium && (
              <>
                <List.Item.Meta description="Base premium" />
                <div>
                  <MoneyDisplay value={insurancePremium} />
                </div>
              </>
            )}
          </Skeleton>
        </List.Item>
        {brokerCommission && (
          <List.Item style={{ padding: 8 }}>
            <Skeleton title={false} loading={!net} active>
              {brokerCommission && (
                <>
                  <List.Item.Meta description="Broker Commission" />
                  {/* Read-only - commission can only be flexed on a quote */}
                  <BrokerCommissionAdjuster
                    amount={brokerCommission}
                    currentCommissionPercent={brokerCommissionPercent}
                  />
                </>
              )}
            </Skeleton>
          </List.Item>
        )}

        <List.Item style={{ padding: 8 }}>
          <Skeleton
            title={false}
            loading={!premiumBreakdown?.value.tax.total}
            active
          >
            {premiumBreakdown?.value.tax.total && (
              <>
                <List.Item.Meta description="Total Tax" />
                <div>
                  <MoneyDisplay value={premiumBreakdown?.value.tax.total} />
                </div>
              </>
            )}
          </Skeleton>
        </List.Item>

        {additionalProducts?.map((re) => {
          const rcLabel =
            RoadsideAssistanceMappings[re.referenceID] || re.referenceID

          return (
            <List.Item style={{ padding: 8 }}>
              <>
                <List.Item.Meta description={rcLabel} />
                <div>
                  <MoneyDisplay value={re.priceGross} />
                </div>
              </>
            </List.Item>
          )
        })}

        {feesTotal && (
          <List.Item style={{ padding: 8 }}>
            <Skeleton title={false} loading={!feesTotal} active>
              <>
                <List.Item.Meta description="Underwriting Fee" />
                <div>
                  <MoneyDisplay value={feesTotal} />
                </div>
              </>
            </Skeleton>
          </List.Item>
        )}
      </List>
    </Card>
  )
}

export interface IQuotePremium {
  loading?: boolean
  gross?: IMoney
  iptPercentage?: number
  iptTotal?: IMoney
  net?: IMoney
  brokerCommission?: IMoney
  brokerCommissionPercent?: number
  premiumBreakdown?: IPremiumBreakdown
  additionalProducts?: IAdditionalProduct[]
  feesTotal?: IMoney
}
